/* ============================================================
   <spring-press> — horizontal article scroller + arrow nav
   ============================================================ */

class SpringPress extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[js-spring-press-track]');
    this.arrows = this.querySelector('.spring-press__arrows');
    this.prevBtn = this.querySelector('[js-spring-press-prev]');
    this.nextBtn = this.querySelector('[js-spring-press-next]');

    if (this.track) {
      this.bindArrows();
      this.bindScroll();
      this.bindKeyboard();
      this.updateArrows();
    }

    this.bindMarqueeToggle();
  }

  // --- Logos marquee pause/play (WCAG 2.2.2: touch users need a control;
  //     hover/focus-within pause doesn't exist on touch devices) ----------

  bindMarqueeToggle() {
    const marquee = this.querySelector('[data-marquee]');
    const toggle = this.querySelector('[data-marquee-toggle]');
    if (!marquee || !toggle) return;

    toggle.addEventListener('click', () => {
      const paused = marquee.classList.toggle('is-paused');
      toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
      toggle.setAttribute('aria-label', paused ? 'Play logos' : 'Pause logos');
    });
  }

  // --- Arrow nav -----------------------------------------------

  bindArrows() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.scrollByDirection(-1));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.scrollByDirection(1));
    }
  }

  bindScroll() {
    this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true });
    window.addEventListener('resize', () => this.updateArrows(), { passive: true });
  }

  bindKeyboard() {
    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.scrollByDirection(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.scrollByDirection(-1);
      }
    });
  }

  getFirstVisibleCard() {
    const cards = this.track.querySelectorAll('.spring-press__card');
    for (const card of cards) {
      if (window.getComputedStyle(card).display !== 'none') return card;
    }
    return null;
  }

  scrollByDirection(direction) {
    if (!this.track) return;
    const card = this.getFirstVisibleCard();
    if (!card) return;

    const cardWidth = card.getBoundingClientRect().width;
    const styles = window.getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap || '0');
    const step = cardWidth + gap;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.track.scrollBy({
      left: step * direction,
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  }

  updateArrows() {
    if (!this.prevBtn && !this.nextBtn) return;
    if (!this.track) return;

    // Hide the arrows entirely when every card already fits (no overflow) —
    // empty string restores the stylesheet's own breakpoint visibility.
    const hasOverflow = this.track.scrollWidth > this.track.clientWidth + 1;
    if (this.arrows) this.arrows.style.display = hasOverflow ? '' : 'none';

    const atStart = this.track.scrollLeft <= 1;
    const atEnd =
      this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 1;

    if (this.prevBtn) {
      this.prevBtn.disabled = atStart;
      this.prevBtn.classList.toggle('is-disabled', atStart);
      this.prevBtn.setAttribute('aria-disabled', atStart ? 'true' : 'false');
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = atEnd;
      this.nextBtn.classList.toggle('is-disabled', atEnd);
      this.nextBtn.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    }
  }
}

if (!customElements.get('spring-press')) {
  customElements.define('spring-press', SpringPress);
}
